import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';

export const PROMPT_TEMPLATES = [
  'customer_support',
  'lead_generation',
  'shopping_assistant',
] as const;
export type PromptTemplateId = (typeof PROMPT_TEMPLATES)[number];

type TriggerVariant = 'muted' | 'secondary' | 'outline';
type TriggerSize = 'xxs' | 'xs' | 'sm';
type DropdownAlign = 'start' | 'end' | 'center';

type Props = {
  // When true, picking a template opens a confirm dialog before calling onApply.
  hasExistingValue: boolean;
  onApply: (templateText: string) => void;
  triggerClassName?: string;
  triggerVariant?: TriggerVariant;
  triggerSize?: TriggerSize;
  dropdownAlign?: DropdownAlign;
  'data-testid'?: string;
};

export const PromptTemplateDropdown = ({
  hasExistingValue,
  onApply,
  triggerClassName,
  triggerVariant = 'muted',
  triggerSize = 'xs',
  dropdownAlign = 'start',
  'data-testid': dataTestId = 'prompt-template-dropdown',
}: Props) => {
  const { t } = useTranslation(['getting_started', 'common']);
  const [pendingTemplateId, setPendingTemplateId] = useState<PromptTemplateId | null>(null);

  const handleSelect = (id: PromptTemplateId) => {
    if (hasExistingValue) {
      setPendingTemplateId(id);
      return;
    }
    onApply(t(`identity_step.templates.${id}.prompt`));
  };

  const confirmReplace = () => {
    if (!pendingTemplateId) return;
    onApply(t(`identity_step.templates.${pendingTemplateId}.prompt`));
    setPendingTemplateId(null);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={triggerVariant}
            size={triggerSize}
            className={triggerClassName}
            data-testid={`${dataTestId}-trigger`}
          >
            {t('identity_step.templates.button')}
            <ChevronDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={dropdownAlign}>
          {PROMPT_TEMPLATES.map((id) => (
            <DropdownMenuItem
              key={id}
              onSelect={() => handleSelect(id)}
              data-testid={`${dataTestId}-item-${id}`}
            >
              {t(`identity_step.templates.${id}.label`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={pendingTemplateId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTemplateId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('identity_step.templates.replace_confirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTemplateId &&
                t('identity_step.templates.replace_confirm.description', {
                  template: t(`identity_step.templates.${pendingTemplateId}.label`),
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="secondary" data-testid={`${dataTestId}-cancel`}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </AlertDialogCancel>
            <Button
              variant="danger"
              onClick={confirmReplace}
              data-testid={`${dataTestId}-confirm-replace`}
            >
              {t('identity_step.templates.replace_confirm.action')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
